import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LogOut, FolderOpen, Plus, Trash2, Edit, RefreshCw, XOctagon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ProjectForm from '../components/ProjectForm';
import DriveSelector from '../components/DriveSelector';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminPage() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);
  const [projects, setProjects] = useState([]);
  const [driveConnected, setDriveConnected] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showDriveSelector, setShowDriveSelector] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  useEffect(() => {
    // Check for Drive connection success
    const params = new URLSearchParams(location.search);
    if (params.get('drive_connected') === 'true') {
      toast.success('Google Drive conectado exitosamente');
      setDriveConnected(true);
    }
    if (params.get('drive_error') === 'true') {
      toast.error('Error al conectar Google Drive');
    }
  }, [location]);

  useEffect(() => {
    // CRITICAL: Skip auth check if returning from OAuth callback
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      return;
    }

    // If user data passed from AuthCallback, skip check
    if (location.state?.user) {
      setIsAuthenticated(true);
      checkDriveStatus();
      fetchProjects();
      return;
    }

    // Check existing session
    checkAuth();
  }, [location.state]);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUser(response.data);
      setIsAuthenticated(true);
      checkDriveStatus();
      fetchProjects();
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      localStorage.removeItem('session_token');
    }
  };

  const checkDriveStatus = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/drive/status`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDriveConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking Drive status:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Poll while any project is still processing
  useEffect(() => {
    const anyProcessing = projects.some(p => p.status === 'processing');
    if (!anyProcessing) return;
    const interval = setInterval(fetchProjects, 4000);
    return () => clearInterval(interval);
  }, [projects]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        await axios.post(
          `${API}/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('session_token');
    setIsAuthenticated(false);
    setUser(null);
    toast.success('Sesión cerrada');
  };

  const handleConnectDrive = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/drive/connect`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error connecting Drive:', error);
      toast.error(error.response?.data?.detail || 'Error al conectar Google Drive');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('¿Estás seguro de eliminar este proyecto?')) return;

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Proyecto eliminado');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Error al eliminar proyecto');
    }
  };

  const handleRetryProject = async (projectId) => {
    const token = localStorage.getItem('session_token');
    try {
      const res = await axios.post(`${API}/projects/${projectId}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Reintentando...');
      fetchProjects();
    } catch (error) {
      console.error('Error retrying:', error);
      toast.error(error.response?.data?.detail || 'No se pudo reintentar');
    }
  };

  const handleResetProject = async (projectId) => {
    if (!window.confirm('¿Forzar finalización? Si el procesamiento sigue corriendo, las fotos restantes no se guardarán.')) return;
    const token = localStorage.getItem('session_token');
    try {
      await axios.post(`${API}/projects/${projectId}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estado actualizado');
      fetchProjects();
    } catch (error) {
      console.error('Error resetting:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setSelectedPhotos(project.photos);
    setShowProjectForm(true);
  };

  const handleCreateProject = () => {
    setEditingProject(null);
    setSelectedPhotos([]);
    setShowProjectForm(true);
  };

  const handleCloseForm = () => {
    setShowProjectForm(false);
    setEditingProject(null);
    setSelectedPhotos([]);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    fetchProjects();
  };

  const handleSelectPhotos = () => {
    setShowDriveSelector(true);
  };

  const handlePhotosSelected = (photos) => {
    // Keep already-persisted photos and add new Drive selections
    const persisted = selectedPhotos.filter(p => p.file_key);
    setSelectedPhotos([...persisted, ...photos]);
    setShowDriveSelector(false);
  };

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-neutral-400">Cargando...</p>
        </div>
      </div>
    );
  }

  // Login page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navigation />
        <div className="min-h-[80vh] flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium text-white">
                Admin
              </h1>
              <p className="text-base text-neutral-400">
                Inicia sesión para gestionar el portafolio
              </p>
            </div>
            <button
              onClick={handleLogin}
              className="w-full px-8 py-4 bg-white text-black font-medium hover:bg-neutral-200"
              data-testid="admin-login-button"
            >
              Iniciar sesión con Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />

      {/* Admin Header */}
      <section className="px-6 sm:px-12 lg:px-24 pt-32 pb-12 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="space-y-2">
            <p className="text-xs tracking-[0.2em] uppercase text-neutral-500">Panel de Control</p>
            <h1 className="text-4xl tracking-tighter font-medium text-white">
              Administración
            </h1>
            <p className="text-sm text-neutral-400">Bienvenido, {user?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-6 py-3 border border-neutral-800 text-neutral-400 hover:text-white hover:border-white"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesión
          </button>
        </div>
      </section>

      {/* Drive Connection */}
      <section className="px-6 sm:px-12 lg:px-24 py-8 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-neutral-400" />
            <span className="text-sm text-neutral-300">
              {driveConnected ? 'Google Drive conectado' : 'Google Drive no conectado'}
            </span>
          </div>
          {!driveConnected && (
            <button
              onClick={handleConnectDrive}
              className="px-6 py-2 bg-white text-black text-sm font-medium hover:bg-neutral-200"
              data-testid="google-drive-connect-btn"
            >
              Conectar Drive
            </button>
          )}
        </div>
      </section>

      {/* Projects Management */}
      <section className="px-6 sm:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl text-white tracking-tight">Proyectos</h2>
            <button
              onClick={handleCreateProject}
              className="flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-neutral-200"
              data-testid="create-project-btn"
            >
              <Plus className="w-4 h-4" />
              Nuevo Proyecto
            </button>
          </div>

          {/* Projects List */}
          <div className="space-y-4">
            {projects.map((project) => {
              const status = project.status || 'ready';
              const isProcessing = status === 'processing';
              const isError = status === 'error';
              const photosDone = (project.photos || []).length;
              const photosTotal = project.photos_total || photosDone;
              const skippedCount = (project.skipped_photos || []).length;
              const firstPhoto = (project.photos || [])[0];

              return (
                <div
                  key={project.project_id}
                  className="bg-neutral-900 border border-neutral-800 p-6 flex items-center justify-between"
                  data-testid={`project-item-${project.project_id}`}
                >
                  <div className="flex items-center gap-6">
                    {firstPhoto ? (
                      <img
                        src={firstPhoto.thumbnail_url || firstPhoto.url}
                        alt={project.title}
                        className="w-20 h-20 object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-neutral-800 flex items-center justify-center">
                        {isProcessing ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <span className="text-neutral-500 text-xs">Sin fotos</span>
                        )}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg text-white font-medium">{project.title}</h3>
                        {isProcessing && (
                          <span className="px-2 py-0.5 bg-amber-600/20 text-amber-400 text-xs flex items-center gap-1.5" data-testid={`status-processing-${project.project_id}`}>
                            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></span>
                            Procesando {photosDone}/{photosTotal}
                          </span>
                        )}
                        {isError && (
                          <span className="px-2 py-0.5 bg-red-600/20 text-red-400 text-xs" data-testid={`status-error-${project.project_id}`}>
                            Error
                          </span>
                        )}
                        {skippedCount > 0 && status === 'ready' && (
                          <span className="px-2 py-0.5 bg-yellow-600/20 text-yellow-400 text-xs" title="Algunas fotos fueron omitidas (>10MB o error)">
                            {skippedCount} omitida(s)
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-neutral-400">{project.classification} • {project.date}</p>
                      <p className="text-sm text-neutral-500 mt-1 line-clamp-1">{project.description}</p>
                      {isError && project.error_message && (
                        <p className="text-xs text-red-400 mt-1">{project.error_message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isError && (project.skipped_photos || []).some(s => s.reason === 'error' || s.reason === 'timeout') && (
                      <button
                        onClick={() => handleRetryProject(project.project_id)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 text-xs"
                        data-testid={`retry-project-${project.project_id}`}
                        title="Reintentar fotos fallidas"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Reintentar
                      </button>
                    )}
                    {isProcessing && (
                      <button
                        onClick={() => handleResetProject(project.project_id)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-neutral-800 text-neutral-300 hover:bg-red-600/20 hover:text-red-400 text-xs"
                        data-testid={`reset-project-${project.project_id}`}
                        title="Forzar finalización (si está atascado)"
                      >
                        <XOctagon className="w-3.5 h-3.5" />
                        Forzar fin
                      </button>
                    )}
                    <button
                      onClick={() => handleEditProject(project)}
                      disabled={isProcessing}
                      className="p-2 text-neutral-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`edit-project-${project.project_id}`}
                      title={isProcessing ? 'Espera a que termine el procesamiento' : 'Editar'}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project.project_id)}
                      className="p-2 text-neutral-400 hover:text-red-400"
                      data-testid={`delete-project-${project.project_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}

            {projects.length === 0 && (
              <div className="text-center py-12 text-neutral-400">
                No hay proyectos creados
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Project Form Modal */}
      {showProjectForm && (
        <ProjectForm
          project={editingProject}
          selectedPhotos={selectedPhotos}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
          onSelectPhotos={handleSelectPhotos}
          onPhotosChange={setSelectedPhotos}
        />
      )}

      {/* Drive Selector Modal */}
      {showDriveSelector && (
        <DriveSelector
          onClose={() => setShowDriveSelector(false)}
          onSelect={handlePhotosSelected}
          initialSelected={selectedPhotos.filter(p => !p.file_key)}
          maxPhotos={15 - selectedPhotos.filter(p => p.file_key).length}
        />
      )}
    </div>
  );
}

export default AdminPage;